import { SetFilterValuesFuncParams } from 'ag-grid-enterprise'
import { BatchSelectionHeader } from 'features/autocheck/utils'
import {
  formatClaimStatusLabel,
  formatDetailedClaimStatusLabel,
  formatStatusWithCode,
  getAppealStatuses,
  getClaimStatuses,
} from 'features/claims/claimsApi'
import moment from 'moment'
import { formatPhoneNumber, formatWorkFlowStatus } from 'utils'
import { formatCoverageStatus, formatLevelofSupport } from 'utils/cellFormat'
import { formatAppealStatusDescription } from 'utils/dataExtractor'
import { formatDateWithAMPM } from 'utils/date'
import { ClaimCallInProgress } from './ClaimCallProgress'
import { currencyFormatter, filterParams } from './ClaimsTableUtils'
const defaultColumnFilterParams = {
  filterOptions: ['contains'],
  maxNumConditions: 1,
  buttons: ['apply', 'cancel'],
  closeOnApply: true,
}

export const TABLE_COLUMNS: { [key: string]: any } = {
  serialNumber: {
    width: 45,
    checkboxSelection: true,
    lockPosition: 'left',
    pinned: 'left',
    headerCheckboxSelection: false,
    showDisabledCheckboxes: true,
    suppressMovable: true,
    field: 'select',
    lockVisible: true,
    headerComponent: BatchSelectionHeader,
  },
  archive: {
    field: 'archived',
    headerName: 'Archived',
    valueFormatter: (params: { value: boolean }) => (params.value === true ? 'Yes' : 'No'),
    hide: true,
    width: 200,
  },

  latest_eob: {
    field: 'latestEOBReceivedDate',
    headerName: 'Latest EOB Rcvd Date',
    valueFormatter: (params: any) => (params.value ? moment(params.value).format('MM/DD/YYYY') : '---'),
    hide: true,
    sortable: true,
    filter: 'agDateColumnFilter',
    lockPinned: true,
    filterParams: {
      filterOptions: ['lessThan', 'greaterThan', 'inRange'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    width: 250,
  },

  rendering_provider: {
    field: 'renderingProvider',
    filter: 'agTextColumnFilter',
    headerName: 'Rendering Provider',
    lockPinned: true,
    hide: true,
    valueFormatter: (params: { value: string }) => params.value || '---',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
  },
  rendering_provider_npi: {
    field: 'renderingProviderNpi',
    headerName: 'Rendering Provider NPI',
    lockPinned: true,
    hide: true,
    sortable: false,
    valueFormatter: (params: { value: string }) => params.value || '---',
  },
  oai_internal_status: {
    field: 'oaiStatus',
    headerName: 'Follow Up Status',
    sortable: false,
    lockPinned: true,
    hide: true,
    valueFormatter: (params: { value: string }) => formatWorkFlowStatus(params.value) || '---',
  },
  member_id: {
    field: 'memberId',
    filter: 'agTextColumnFilter',
    headerName: 'Member Id',
    lockPinned: true,
    hide: true,
    floatingFilter: false,
    suppressFiltersToolPanel: true,
    headerClass: 'externalized-filter',
    valueFormatter: (params: { value: string }) => params.value || '---',
    filterParams: {
      filterOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
  },
  patient_id: {
    field: 'patientId',
    headerName: 'Patient Id',
    filter: 'agTextColumnFilter',
    lockPinned: true,
    hide: true,
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    valueFormatter: (params: any) => params.value || '---',
  },
  visit_id: {
    field: 'visitId',
    headerName: 'Account ID',
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    suppressMovable: true,
    lockPinned: true,
    lockPosition: 'left',
    lockVisible: true,
    floatingFilter: false,
    suppressFiltersToolPanel: true,
    headerClass: 'externalized-filter',
    pinned: 'left',
    cellRenderer: ClaimCallInProgress,
  },
  location: {
    field: 'location',
    filter: 'agSetColumnFilter',
    headerName: 'Location',
    lockPinned: true,
    width: 150,
    valueFormatter: (params: any) => params.value || '---',
    filterParams: filterParams,
  },
  biller_npi: {
    field: 'billerNpi',
    headerName: 'Billing NPI',
    filter: 'agTextColumnFilter',
    lockPinned: true,
    hide: true,
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    valueFormatter: (params: any) => params.value || '---',
  },
  biller_name: {
    field: 'billerName',
    headerName: 'Billing Entity Name',
    filter: 'agTextColumnFilter',
    lockPinned: true,
    hide: true,
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    valueFormatter: (params: any) => params.value || '---',
  },
  callInformation_taxId: {
    field: 'billerTaxId',
    headerName: 'Billing Tax Id',
    filter: 'agTextColumnFilter',
    lockPinned: true,
    hide: true,
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    valueFormatter: (params: any) => params.value || '---',
    slot: 'callInformation_taxId', // in JSON
  },

  patient_first_name: {
    field: 'patientFirstName',
    headerName: 'Patient First Name',
    lockPinned: true,
    hide: true,
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: { ...defaultColumnFilterParams },
    valueFormatter: (params: any) => params.value || '---',
  },
  patient_last_name: {
    field: 'patientLastName',
    headerName: 'Patient Last Name',
    lockPinned: true,
    sortable: true,
    floatingFilter: false,
    suppressFiltersToolPanel: true,
    headerClass: 'externalized-filter',
    filter: 'agTextColumnFilter',
    hide: true,
    valueFormatter: (params: any) => params.value || '---',
  },
  patient_dob: {
    field: 'patientDob',
    headerName: 'Patient DOB',
    sortable: true,
    filter: 'agDateColumnFilter',
    lockPinned: true,
    hide: true,
    filterParams: {
      filterOptions: ['lessThan', 'greaterThan', 'inRange'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    valueFormatter: (params: any) => (params.value ? moment(params.value).format('MM/DD/YYYY') : '---'),
  },
  date_of_provision_or_admission: {
    field: 'admitDate',
    headerName: 'Admit Date',
    filter: 'agDateColumnFilter',
    lockPinned: true,
    hide: true,
    floatingFilter: false,
    suppressFiltersToolPanel: true,
    headerClass: 'externalized-filter',
    filterParams: {
      filterOptions: ['lessThan', 'greaterThan', 'inRange'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    valueFormatter: (params: any) => (params.value ? moment(params.value).format('MM/DD/YYYY') : '---'),
  },
  discharge_date: {
    field: 'dischargeDate',
    headerName: 'Discharge Date',
    sortable: false,
    hide: true,
    lockPinned: true,
    valueFormatter: (params: any) => (params.value ? moment(params.value).format('MM/DD/YYYY') : '---'),
  },
  patient_state: {
    field: 'patientState',
    headerName: 'Patient State',
    filter: 'agSetColumnFilter',
    lockPinned: true,
    hide: true,
    filterParams: filterParams,
    valueFormatter: (params: any) => params.value || '---',
  },
  patient_zip: {
    field: 'patientZip',
    headerName: 'Patient ZIP Code',
    sortable: false,
    lockPinned: true,
    hide: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains', 'equals', 'startsWith'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    valueFormatter: (params: any) => params.value || '---',
  },
  oai_worked: {
    field: 'oaiWorked',
    headerName: 'Last Modified',
    filter: 'agDateColumnFilter',
    lockPinned: true,
    floatingFilter: false,
    suppressFiltersToolPanel: true,
    headerClass: 'externalized-filter',
    filterParams: {
      filterOptions: ['lessThan', 'greaterThan', 'inRange'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    valueFormatter: (params: any) => {
      return formatDateWithAMPM(params.value)
    },
  },
  insurer_id: {
    field: 'insurerId',
    headerName: 'Insurance Id',
    filter: 'agTextColumnFilter',
    lockPinned: true,
    hide: true,
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    valueFormatter: (params: any) => params.value || '---',
  },
  insurer_name: {
    field: 'insurerName',
    headerName: 'Insurer Name',
    filter: 'agTextColumnFilter',
    lockPinned: true,
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    valueFormatter: (params: any) => params.value || '---',
  },
  insurer_phone_number: {
    field: 'insurerPhoneNumber',
    headerName: 'Insurance Phone Number',
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: { ...defaultColumnFilterParams },
    lockPinned: true,
    hide: true,
    valueFormatter: (params: any) => {
      return formatPhoneNumber(params.value)
    },
  },
  appeal_phone_number: {
    field: 'appealPhoneNumber',
    headerName: 'Appeal Phone Number',
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: { ...defaultColumnFilterParams },
    lockPinned: true,
    hide: true,
    valueFormatter: (params: any) => {
      return formatPhoneNumber(params.value)
    },
  },
  tpa_name: {
    field: 'tpaName',
    headerName: 'TPA Name',
    filter: 'agTextColumnFilter',
    lockPinned: true,
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    valueFormatter: (params: any) => params.value || '---',
  },
  tpa_phone_number: {
    field: 'tpaPhoneNumber',
    headerName: 'TPA Phone Number',
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: { ...defaultColumnFilterParams },
    lockPinned: true,
    hide: true,
    valueFormatter: (params: any) => {
      return formatPhoneNumber(params.value)
    },
  },
  insurer_plan_name: {
    field: 'insurerPlanName',
    headerName: 'Insurance Plan',
    filter: 'agTextColumnFilter',
    lockPinned: true,
    hide: true,
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    valueFormatter: (params: any) => params.value || '---',
  },
  subscriber_first_name: {
    field: 'subscriberFirstName',
    headerName: 'Subs. First Name',
    lockPinned: true,
    sortable: false,
    hide: true,
    valueFormatter: (params: any) => params.value || '---',
  },
  subscriber_last_name: {
    field: 'subscriberLastName',
    headerName: 'Subs. Last Name',
    lockPinned: true,
    sortable: false,
    hide: true,
    valueFormatter: (params: any) => params.value || '---',
  },
  subscriber_dob: {
    field: 'subscriberDob',
    headerName: 'Subs. DOB',
    lockPinned: true,
    sortable: false,
    valueFormatter: (params: any) => (params.value ? moment(params.value).format('MM/DD/YYYY') : '---'),
    hide: true,
  },
  subscriber_id: {
    field: 'subscriberId',
    headerName: 'Subscriber Id',
    lockPinned: true,
    sortable: false,
    hide: true,
    valueFormatter: (params: any) => params.value || '---',
  },
  group_id: {
    field: 'groupId',
    headerName: 'Group Id',
    lockPinned: true,
    sortable: false,
    hide: true,
    valueFormatter: (params: any) => params.value || '---',
  },
  insurer_claim_id: {
    field: 'insurerClaimId',
    headerName: 'Claim ID',
    filter: 'agTextColumnFilter',
    lockPinned: true,
    hide: true,
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
  },
  claim_submission_date: {
    field: 'claimSubmissionDate',
    headerName: 'Claim Submission ',
    filter: 'agDateColumnFilter',
    lockPinned: true,
    hide: true,
    filterParams: {
      filterOptions: ['lessThan', 'greaterThan', 'inRange'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    valueFormatter: (params: any) => (params.value ? moment(params.value).format('MM/DD/YYYY') : '---'),
  },
  claim_billed_amount: {
    field: 'claimAmount',
    headerName: 'Claim Amount',
    lockPinned: true,
    sorting: true,
    hide: true,
    filter: 'agNumberColumnFilter',
    filterParams: {
      filterOptions: ['lessThanOrEqual', 'greaterThanOrEqual', 'inRange'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    valueFormatter: currencyFormatter,
  },
  outstanding_balance: {
    field: 'outstandingBalance',
    headerName: 'Balance Due',
    filter: 'agNumberColumnFilter',
    lockPinned: true,
    filterParams: {
      filterOptions: ['lessThanOrEqual', 'greaterThanOrEqual'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    valueFormatter: currencyFormatter,
  },
  auth_id: {
    field: 'authId',
    headerName: 'Auth Id',
    lockPinned: true,
    hide: true,
    sortable: false,
    valueFormatter: (params: any) => params.value || '---',
  },
  auth_date: {
    field: 'authDate',
    headerName: 'Auth Date',
    lockPinned: true,
    hide: true,
    sortable: false,
    valueFormatter: (params: any) => (params.value ? moment(params.value).format('MM/DD/YYYY') : '---'),
  },
  rcm_agent_owner: {
    field: 'owner',
    filter: 'agSetColumnFilter',
    headerName: 'Owner',
    lockPinned: true,
    floatingFilter: false,
    suppressFiltersToolPanel: true,
    headerClass: 'externalized-filter',
    width: 150,
    valueFormatter: (params: any) => params.value || '---',
    filterParams: filterParams,
  },
  levelofSupport: {
    field: 'levelOfSupport',
    headerName: 'Level of Support',
    filter: 'agSetColumnFilter',
    hide: true,
    lockPinned: true,
    cellRenderer: (params: any) => {
      return formatLevelofSupport(params.value)
    },
    width: 430,
    filterParams: filterParams,
  },
  ptan: {
    field: 'ptan',
    headerName: 'PTAN',
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    lockPinned: true,
    hide: true,
    sortable: false,
    valueFormatter: (params: any) => params.value || '---',
  },
  unique_identifier: {
    field: 'uniqueIdentifier',
    headerName: 'Unique Identifier',
    filter: 'agTextColumnFilter',
    lockPinned: true,
    floatingFilter: false,
    suppressFiltersToolPanel: true,
    headerClass: 'externalized-filter',
  },
  batch_code: {
    field: 'batchCode',
    headerName: 'Batch ID',
    filter: 'agNumberColumnFilter',
    lockPinned: true,
    floatingFilter: false,
    suppressFiltersToolPanel: true,
    headerClass: 'externalized-filter',
    filterParams: {
      filterOptions: ['equals', 'lessThanOrEqual', 'greaterThanOrEqual'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
  },
  ebv_last_verified_date_payer_ivr: {
    field: 'ebvLastVerifiedDate',
    headerName: 'Last Verified Date',
    lockPinned: true,
    sortable: false,
    hide: true,
    valueFormatter: (params: any) => params.value || 'Not checked',
  },
  ebv_coverage_status_payer_ivr: {
    field: 'ebvCoverageStatus',
    headerName: 'Coverage Status',
    filter: 'agSetColumnFilter',
    lockPinned: true,
    hide: true,
    cellRenderer: (params: any) => {
      return formatCoverageStatus(params.value)
    },
    width: 295,
    filterParams: filterParams,
  },
  claim_status_payer: {
    field: 'claimStatusPayer',
    filter: 'agSetColumnFilter',
    headerName: 'Extracted Claim Status',
    lockPinned: true,
    floatingFilter: false,
    suppressFiltersToolPanel: true,
    headerClass: 'externalized-filter',
    width: 200,
    valueFormatter: (params: { value: string }) => params.value || '---',
    cellRenderer: (params: any) =>
      params.value ? formatStatusWithCode(params.data?.claimStatusCode, params.value) : '---',
    filterParams: {
      values: async (params: SetFilterValuesFuncParams) => params.success([...(await getClaimStatuses()), 'Blank']),
      valueFormatter: (params: any) => (params.value == null ? null : formatClaimStatusLabel(params.value)),
      suppressSelectAll: false,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
  },
  detailed_claim_status: {
    field: 'detailedClaimStatus',
    filter: 'agSetColumnFilter',
    headerName: 'Extracted Status Details',
    sortable: true,
    width: 200,
    valueFormatter: (params: { value: string }) => params.value || '---',
    cellRenderer: (params: any) =>
      params.value ? formatStatusWithCode(params.data?.detailedClaimStatusCode, params.value) : '---',
    filterParams: {
      ...filterParams,
      valueFormatter: (params: any) => (params.value == null ? null : formatDetailedClaimStatusLabel(params.value)),
    },
  },
  provider_street1: {
    field: 'providerStreet1',
    headerName: 'Provider Street 1',
    filter: 'agTextColumnFilter',
    lockPinned: true,
    filterParams: {
      filterOptions: ['contains', 'equals', 'startsWith'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
  },
  provider_street2: {
    field: 'providerStreet2',
    headerName: 'Provider Street 2',
    filter: 'agTextColumnFilter',
    lockPinned: true,
    filterParams: {
      filterOptions: ['contains', 'equals', 'startsWith'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
  },
  provider_city: {
    field: 'providerCity',
    headerName: 'Provider City',
    filter: 'agTextColumnFilter',
    lockPinned: true,
    filterParams: {
      filterOptions: ['contains', 'equals', 'startsWith'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
  },
  provider_state: {
    field: 'providerState',
    headerName: 'Provider State',
    filter: 'agTextColumnFilter',
    lockPinned: true,
    filterParams: {
      filterOptions: ['contains', 'equals', 'startsWith'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
  },
  provider_zip_code: {
    field: 'providerZipCode',
    headerName: 'Provider ZIP Code',
    filter: 'agTextColumnFilter',
    lockPinned: true,
    filterParams: {
      filterOptions: ['contains', 'equals', 'startsWith'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
  },
  owcp_provider_id: {
    field: 'owcpProviderId',
    headerName: 'OWCP Provider ID',
    filter: 'agTextColumnFilter',
    lockPinned: true,
    hide: true,
    filterParams: {
      filterOptions: ['contains', 'equals', 'startsWith'],
      maxNumConditions: 1,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
    valueFormatter: (params: any) => params.value || '---',
  },
  claim_id: {
    field: 'claimId',
    headerName: 'Claim ID',
    hide: true,
  },
  // TODO: mock/dummy columns until the appeal fields are backed by real entities
  payer_appeal_case_number: {
    field: 'appealPayerCaseNumber',
    headerName: 'Payer Appeal Case Number',
    filter: false,
    sortable: false,
    lockPinned: true,
    hide: true,
    valueFormatter: (params: any) => params.value || '---',
  },
  appeal_submission_date: {
    field: 'appealSubmissionDate',
    headerName: 'Appeal Submission Date',
    filter: false,
    sortable: false,
    lockPinned: true,
    hide: true,
    valueFormatter: (params: any) => (params.value ? moment(params.value).format('MM/DD/YYYY') : '---'),
  },
  appeal_submission_method: {
    field: 'appealSubmissionMethod',
    headerName: 'Appeal Submission Method',
    filter: false,
    sortable: false,
    lockPinned: true,
    hide: true,
    valueFormatter: (params: any) => {
      if (params.value == null || params.value === '') return '---'
      return params.context?.appealSubmissionMethodDescriptions?.[params.value] ?? params.value
    },
  },
  appeal_submission_tracking_number: {
    field: 'appealSubmissionTrackingNumber',
    headerName: 'Appeal Submission Tracking Number',
    filter: false,
    sortable: false,
    lockPinned: true,
    hide: true,
    valueFormatter: (params: any) => params.value || '---',
  },
  provider_appeal_fax_number: {
    field: 'providerAppealFaxNumber',
    headerName: 'Provider Appeal Fax Number',
    filter: false,
    sortable: false,
    lockPinned: true,
    hide: true,
    valueFormatter: (params: any) => formatPhoneNumber(params.value),
  },
  provider_appeal_street1: {
    field: 'providerAppealStreet1',
    headerName: 'Provider Appeal Street 1',
    filter: false,
    sortable: false,
    lockPinned: true,
    hide: true,
    valueFormatter: (params: any) => params.value || '---',
  },
  provider_appeal_street2: {
    field: 'providerAppealStreet2',
    headerName: 'Provider Appeal Street 2',
    filter: false,
    sortable: false,
    lockPinned: true,
    hide: true,
    valueFormatter: (params: any) => params.value || '---',
  },
  provider_appeal_city: {
    field: 'providerAppealCity',
    headerName: 'Provider Appeal City',
    filter: false,
    sortable: false,
    lockPinned: true,
    hide: true,
    valueFormatter: (params: any) => params.value || '---',
  },
  provider_appeal_state: {
    field: 'providerAppealState',
    headerName: 'Provider Appeal State',
    filter: false,
    sortable: false,
    lockPinned: true,
    hide: true,
    valueFormatter: (params: any) => params.value || '---',
  },
  provider_appeal_zipcode: {
    field: 'providerAppealZipCode',
    headerName: 'Provider Appeal ZIP Code',
    filter: false,
    sortable: false,
    lockPinned: true,
    hide: true,
    valueFormatter: (params: any) => params.value || '---',
  },
  level_of_appeal: {
    field: 'levelOfAppeal',
    headerName: 'Level of Appeal',
    filter: false,
    sortable: false,
    lockPinned: true,
    hide: true,
    valueFormatter: (params: any) => {
      if (params.value == null || params.value === '') return '---'
      return params.context?.levelOfAppealDescriptions?.[params.value] ?? params.value
    },
  },
  appeal_status: {
    field: 'appealStatus',
    headerName: 'Appeal Status',
    filter: 'agSetColumnFilter',
    lockPinned: true,
    floatingFilter: false,
    suppressFiltersToolPanel: true,
    headerClass: 'externalized-filter',
    width: 200,
    valueFormatter: (params: any) => {
      if (params.value == null || params.value === '') return '---'
      const description = params.context?.appealStatusDescriptions?.[params.value]
      return description ? formatAppealStatusDescription(description) : params.value
    },
    filterParams: {
      values: async (params: SetFilterValuesFuncParams) => params.success([...(await getAppealStatuses()), 'Blank']),
      suppressSelectAll: false,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
  },
}

const BASE_CLAIM_SLOTS = [
  'visit_id',
  'rcm_agent_owner',
  'location',
  'claim_status_payer',
  'detailed_claim_status',
  'levelofSupport',
  'patient_dob',
  'claim_submission_date',
  'claim_billed_amount',
  'oai_worked',
  'insurer_name',
  'insurer_plan_name',
  'insurer_phone_number',
  'rendering_provider',
  'rendering_provider_npi',
  'provider_street1',
  'provider_street2',
  'provider_city',
  'provider_state',
  'provider_zip_code',
  'biller_npi',
  'callInformation_taxId',
  'biller_name',
  'ptan',
  'member_id',
  'patient_id',
  'patient_first_name',
  'patient_last_name',
  'patient_state',
  'patient_zip',
  'auth_date',
  'date_of_provision_or_admission',
  'oai_internal_status',
  'unique_identifier',
  'batch_code',
  'latest_eob',
  'owcp_provider_id',
  'tpa_name',
  'tpa_phone_number',
  'payer_appeal_case_number',
  'appeal_submission_date',
  'appeal_submission_method',
  'appeal_submission_tracking_number',
  'provider_appeal_fax_number',
  'provider_appeal_street1',
  'provider_appeal_street2',
  'provider_appeal_city',
  'provider_appeal_state',
  'provider_appeal_zipcode',
  'level_of_appeal',
  'appeal_phone_number',
  'appeal_status',
] as const

// Table-specific variations
export const CLAIMS_SLOTS = ['serialNumber', ...BASE_CLAIM_SLOTS]

export const CLAIMS_TABLE = CLAIMS_SLOTS.map((k) => TABLE_COLUMNS[k])
